// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import Tooltip from "@bg/MobiusOverrides/Tooltip/Tooltip";
import { styledLinksMenu, xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import { FormFieldPresentationProps } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Select, { SelectComponentProps } from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import {
    appendLinkElement,
    getCanonical,
    getPrevCurrentNextQueryString,
    hasLinksWithRelPrevNext,
    removeLinkElement,
    updateCanonical,
} from "@insite/mobius/utilities/linkElementsManipulation";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import * as React from "react";
import { useState } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import { Instance } from "tippy.js";

export interface PaginationPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        currentButton?: StyledProp<PaginationProps>;
        arrowButtons?: StyledProp<PaginationProps>;
        ellipsis?: StyledProp<PaginationProps>;
        linkList?: StyledProp<PaginationProps>;
        pagination?: StyledProp<PaginationProps>;
        perPageSelect?: StyledProp<PaginationProps>;
    };
    /** An object containing props to be passed down to the button component inside pagination.
     * @themable */
    buttonProps?: ButtonPresentationProps;
    /** The button variant that should be used for the current button.
     * @themable */
    currentPageButtonVariant?: ButtonVariants;
    /** An object containing props to be passed down to the select component inside pagination
     * @themable */
    selectProps?: FormFieldPresentationProps<SelectComponentProps>;
    /** An object containing icon sources for the navigation button icons.
     * @themable */
    navIconsSrc?: {
        firstPage?: React.ComponentType | string;
        previousPage?: React.ComponentType | string;
        nextPage?: React.ComponentType | string;
        lastPage?: React.ComponentType | string;
    };
}

export type PaginationComponentProps = MobiusStyledComponentProps<
    "div",
    {
        pageSizeCookie?: string;
        /** The index of the current page of results being displayed */
        currentPage: number;
        /** String describing the "results per page" select */
        resultsPerPageLabel?: string;
        /** OnChange function passed to the select component governing the number of results per page. Receives the event as an argument. */
        onChangeResultsPerPage: (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => void;
        /** Function to build href passed to each button in the pagination button/link list. Receives the page index as an argument. */
        createHref?: (page: number) => void;
        /** OnChange function passed to each button in the pagination button/link list. Receives the page index as an argument. */
        onChangePage: (page: number) => void;
        /** Number of results currently being displayed on each page */
        resultsPerPage: number;
        /** Number of total results to be displayed within the pagination component */
        resultsCount: number;
        /** Options to be displayed under the "results per page" select */
        resultsPerPageOptions: number[];
        /** String describing the URL of the previous page in the series */
        prevPage?: string;
        /** String describing the URL of the next page in the series */
        nextPage?: string;
        /* Controls if we wanted to show the totalCount in the pagination or not  */
        totalCountLabelConfig?: TotalCountLabelConfig;
        /* if true it will only show the page numbers */
        isBottom?: boolean;
        /** flag to hide perPage dropdown */
        hidePerPage?: boolean;
    }
>;

export interface TotalCountLabelConfig {
    showTotal: boolean;
    singularLabel?: string;
    pluralLabel?: string;
}

export type PaginationProps = PaginationPresentationProps & PaginationComponentProps & ThemeProps<BaseTheme>;

const numericButtonStyles = css`
    padding: 0 8px;
`;

const PaginationStyle = styled.div<InjectableCss>`
    width: 100%;
    display: flex;
    align-items: center;
    ${injectCss}
`;

const LinkList = styled.nav<InjectableCss>`
    display: flex;
    justify-content: flex-end;
    white-space: nowrap;
    flex: 1;
    ul {
        display: flex;
        align-items: center;
        list-style: none;
    }
    li {
        &:first-child,
        &:last-child {
            height: 16px;
        }
    }

    ${injectCss}
`;

const PerPageSelect = styled.span<InjectableCss>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-right: 20px;
    flex: 1;

    select {
        height: 32px;
        max-width: 95px;
        min-width: 65px;
        width: auto;
    }

    select + span {
        top: 8px !important;
        padding: 1px 4px !important;
        right: -6px !important;
    }

    ${xScreen("lte", "xs")} {
        flex: 0;

        .page-size__show {
            display: none;
        }
    }

    ${injectCss}
`;

const PagesCountContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    ${xScreen("lte", "xs")} {
        flex: 0;
    }

    ${({ theme }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    flex-direction: column;
                    & > ${/* sc-selector */ TypographyStyle} {
                        margin-bottom: 10px;
                    }
                `,
                null,
                null,
                null,
                null,
            ],
            "max",
        )};
`;

/**
 * Pagination is an interactive component that provides a navigation interface for paged content.
 */
const Pagination: React.FC<PaginationProps> = withTheme(props => {
    const {
        createHref,
        currentPage: possibleStringCurrentPage,
        onChangeResultsPerPage,
        onChangePage,
        resultsPerPage: possibleStringResultsPerPage,
        resultsPerPageLabel,
        resultsCount,
        resultsPerPageOptions,
        pageSizeCookie,
        mergeCss,
        prevPage,
        nextPage,
        totalCountLabelConfig,
        isBottom,
        hidePerPage,
        ...otherProps
    } = props;

    React.useEffect(() => {
        const { prevQueryString, currentQueryString, nextQueryString } = getPrevCurrentNextQueryString(
            prevPage,
            nextPage,
            convertToNumberIfString(possibleStringCurrentPage),
            "",
        );
        const prevCanonical = `${getCanonical()}${prevQueryString}`;
        const nextCanonical = `${getCanonical()}${nextQueryString}`;
        updateCanonical(currentQueryString);
        const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
        const prevNotFromProducts = prevPage?.indexOf("/api/v2/products?") < 0 && prevPage?.indexOf("search=") < 0;
        const nextNotFromProducts = nextPage?.indexOf("/api/v2/products?") < 0 && nextPage?.indexOf("search=") < 0;

        if (prevPage && !hasPrev && prevNotFromProducts) {
            appendLinkElement("prev", prevCanonical);
        }

        if (nextPage && !hasNext && nextNotFromProducts) {
            appendLinkElement("next", nextCanonical);
        }

        return () => {
            const { hasPrev, hasNext } = hasLinksWithRelPrevNext(prevCanonical, nextCanonical);
            if (prevPage && hasPrev && prevNotFromProducts) {
                removeLinkElement("prev", prevCanonical);
            }

            if (nextPage && hasNext && nextNotFromProducts) {
                removeLinkElement("next", nextCanonical);
            }
        };
    }, [prevPage, nextPage]);

    const currentPage = convertToNumberIfString(possibleStringCurrentPage);
    const resultsPerPage = convertToNumberIfString(possibleStringResultsPerPage);

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        if (pageSizeCookie) {
            setCookie(pageSizeCookie, value);
        }

        onChangeResultsPerPage(event, parseInt(value, 10));
        event.currentTarget.blur();
    };

    const { spreadProps, applyProp, applyStyledProp } = applyPropBuilder(otherProps, { component: "pagination" });
    const cssOverrides = spreadProps("cssOverrides" as any);
    const buttonProps = spreadProps("buttonProps" as any);
    const currentPageButtonVariant = applyProp("currentPageButtonVariant");
    const translate = otherProps.theme.translate;
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.pagination?.defaultProps?.mergeCss;
    const numberOfPages = Math.ceil(resultsCount / resultsPerPage);
    const finalPageIndex = Math.floor((resultsCount - 1) / resultsPerPage) + 1;
    const pagesToDisplay: ("e" | number)[] = [];

    for (let i = 0; i <= numberOfPages; i++) {
        pagesToDisplay.push(i);
    }

    const [tooltipInstance, setTooltipInstance] = useState<Instance | null>(null);
    const tooltipPages: any = [];
    for (let pageIndex = 1; pageIndex <= finalPageIndex; pageIndex += 1) {
        tooltipPages.push(
            <Link
                key={pageIndex}
                onClick={() => {
                    if (props.onChangePage) {
                        props.onChangePage?.(pageIndex);
                        tooltipInstance?.hide();
                    }
                }}
                disabled={currentPage === pageIndex}
                css={css`
                    &:hover,
                    &focus,
                    &:active {
                        &,
                        span {
                            text-decoration: none !important;
                            background-color: ${getColor("common.ui4")} !important;
                            font-size: 13px;
                            line-height: 22px;
                        }
                    }
                `}
            >
                {`${translate("Page")} ${pageIndex}`}
            </Link>,
        );
    }

    const triggerLink = (
        <Link
            css={css`
                min-width: 40px;
                width: 40px;
                height: 32px;
                background: ${getColor("common.ui4")};
                display: inline-flex;
                justify-content: center;
                margin-left: 5px;
                margin-right: 5px;
                padding: 5px;
                position: relative;

                &:hover,
                &:focus {
                    &,
                    * {
                        text-decoration: none !important;
                    }
                }
            `}
        >
            <Typography size={14} lineHeight="18px" color="text.main">
                {currentPage}
            </Typography>
        </Link>
    );

    const paginationButtons = (
        <li>
            <StyledWrapper
                css={css`
                    span[class^="TooltipTriggerer"] {
                        svg {
                            transition-duration: 300ms;
                        }
                        &[aria-expanded="true"] {
                            svg {
                                transform: rotate(180deg);
                            }
                        }
                    }
                `}
            >
                <Tooltip
                    text={tooltipPages}
                    trigger="mouseenter focus"
                    onOpen={({ instance }: any) => setTooltipInstance(instance)}
                    placement="bottom-start"
                    triggerAltText={translate("All Pages")}
                    cssOverrides={{
                        tooltipContainer: css`
                            ${styledLinksMenu(120)}
                        `,
                    }}
                    triggerComponent={triggerLink}
                ></Tooltip>
                <Typography
                    size={15}
                    lineHeight="18px"
                    css={css`
                        margin-right: 5px;
                        font-weight: bold;
                    `}
                    color="text.main"
                >
                    of {tooltipPages.length}
                </Typography>
            </StyledWrapper>
        </li>
    );

    const forwardDisabled = currentPage === finalPageIndex || finalPageIndex === 1;
    const backDisabled = currentPage === 1 || finalPageIndex === 1;

    const navButton = (
        pageIndex: number,
        buttonLabel: string,
        color: string,
        disabled: boolean,
        dataTestSelector?: string,
        prev?: boolean,
    ) => (
        <Link
            type="button"
            {...buttonProps}
            css={css`
                ${buttonProps.css}
                padding: 0 10px;
                height: 16px;
                &,
                > span {
                    line-height: 8px;
                }
            `}
            aria-label={buttonLabel}
            typographyProps={{
                ...buttonProps.typographyProps,
            }}
            onClick={() => onChangePage(pageIndex)}
            disabled={disabled}
            {...(dataTestSelector && { "data-test-selector": dataTestSelector })}
        >
            {prev && (
                <Icon
                    src="ChevronDown"
                    color="inherit"
                    size={16}
                    css={css`
                        svg {
                            transform: rotate(90deg);
                        }
                    `}
                />
            )}
            {!prev && (
                <Icon
                    src="ChevronDown"
                    color="inherit"
                    size={16}
                    css={css`
                        svg {
                            transform: rotate(-90deg);
                        }
                    `}
                />
            )}
        </Link>
    );

    const firstItem = (currentPage - 1) * resultsPerPage + 1;
    const lastItem = Math.min(currentPage * resultsPerPage, resultsCount);

    if (resultsCount <= Math.min(resultsPerPageOptions[0], resultsPerPage) && !isBottom) {
        return null;
    }

    return (
        <PaginationStyle
            css={applyStyledProp(["cssOverrides", "pagination"], resolvedMergeCss)}
            {...omitSingle(otherProps, "cssOverrides")}
        >
            {!isBottom && !hidePerPage && (
                <PerPageSelect css={applyStyledProp(["cssOverrides", "perPageSelect"], resolvedMergeCss)}>
                    <Typography
                        size={14}
                        weight="bold"
                        lineHeight="18px"
                        css={css`
                            letter-spacing: 0.48px;
                            margin-right: 6px;
                            white-space: nowrap;
                            @media (max-width: 481px) {
                                display: none;
                            }
                        `}
                        className="page-size__show"
                    >
                        {translate("Show")}
                    </Typography>
                    <Select
                        onChange={changeResultsPerPage}
                        label="sd"
                        labelPosition="left"
                        preventEllipsis={true}
                        value={resultsPerPage}
                        {...spreadProps("selectProps" as any)}
                        data-test-selector="pagination_resultsPerPage"
                        sizeVariant="small"
                    >
                        {resultsPerPageOptions.map((n: string) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </Select>
                </PerPageSelect>
            )}
            {pagesToDisplay.length > 1 && (
                <PagesCountContainer>
                    <LinkList
                        role="navigation"
                        aria-label={translate("Pagination navigation")}
                        css={applyStyledProp(["cssOverrides", "linkList"], resolvedMergeCss)}
                        data-test-selector={`pagination_currentPage_${currentPage}`}
                    >
                        <ul>
                            <li>
                                {navButton(
                                    currentPage - 1,
                                    translate("Prev"),
                                    "secondary",
                                    backDisabled,
                                    "paginationButtonPrevious",
                                    true,
                                )}
                            </li>
                            {paginationButtons}
                            <li>
                                {navButton(
                                    currentPage + 1,
                                    translate("Next"),
                                    "primary",
                                    forwardDisabled,
                                    "paginationButtonNext",
                                    false,
                                )}
                            </li>
                        </ul>
                    </LinkList>
                </PagesCountContainer>
            )}
        </PaginationStyle>
    );
});

/** @component */
export default withTheme(Pagination) as React.ComponentType<PaginationComponentProps>;

export { Pagination, PaginationStyle };
